import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  MenuItem,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  LibraryAdd as LibraryAddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { coursesAPI, testsAPI, modulesAPI, lessonsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import JsonImportExport from '../components/UIKit/JsonImportExport';
import Button from '../components/UIKit/Button';
import TextField from '../components/UIKit/TextField';
import Select from '../components/UIKit/Select';
import Card from '../components/UIKit/Card';
import CustomAutocomplete from '../components/UIKit/CustomAutocomplete';
import libraryEventsService from '../services/libraryEventsService';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchTools, selectAllTools, selectToolsLoading } from '../redux/slices/toolsSlice';
import { fetchSkills } from '../redux/slices/skillsSlice';
import LessonSelector from '../components/courses/LessonSelector';
import ModuleSelector from '../components/courses/ModuleSelector';
import ModuleCard from '../components/courses/ModuleCard';
import HeroSection from '../components/courses/HeroSection';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const CourseEdit = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { t } = useTranslation(['courses', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [courseData, setCourseData] = useState({
    title: '',
    description: '',
    difficulty: 'beginner',
    image: '',
    imagePreview: '',
    videos: [], // Array of video URLs
    professions: [],
    tools: [],
    modules: [],
  });
  const [courseId, setCourseId] = useState('');
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [isDraft, setIsDraft] = useState(false);
  const [manualProfessions, setManualProfessions] = useState(new Set()); // Профессии, добавленные вручную
  const [professionsInitialized, setProfessionsInitialized] = useState(false); // Флаг инициализации профессий
  const [lessonSelectorOpen, setLessonSelectorOpen] = useState(false);
  const [moduleSelectorOpen, setModuleSelectorOpen] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(null);
  const [heroPreviewOpen, setHeroPreviewOpen] = useState(false); // For hero preview
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [, startTransition] = useTransition();

  // Состояние для отслеживания удаленных уроков: Map<lessonId, moduleId>
  const [deletedLessonsMap, setDeletedLessonsMap] = useState(new Map());

  // Состояние для модального окна подтверждения удаления модуля
  const [deleteModuleDialog, setDeleteModuleDialog] = useState({
    open: false,
    moduleIndex: null,
    moduleId: null,
    moduleTitle: '',
    loading: false
  });

  // Локальное состояние для полей ввода с debouncing
  const [localFormData, setLocalFormData] = useState({
    title: '',
    description: '',
    difficulty: 'beginner',
    image: '',
    videoUrl: ''
  });

  // Локальное состояние для полей модулей
  const [localModuleFields, setLocalModuleFields] = useState({});

  // Refs для debouncing
  const debounceRefs = useRef({
    title: null,
    description: null,
    image: null,
    videoUrl: null,
    modules: {}
  });

  // Используем данные из Redux вместо локального состояния
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const tools = useSelector(selectAllTools);
  const toolsLoading = useSelector(selectToolsLoading);

  // Функция загрузки данных курса
  const fetchData = useCallback(async () => {
    if (!slug || !user) {
      return;
    }
    
    try {
      setLoading(true);
      // Загружаем данные курса
      const response = await coursesAPI.getBySlugWithProfessions(slug, { isCommon: 1 });
      const data = response.data;
      
      // Сначала проверяем права admin и teacher - они могут редактировать любые курсы
      if (user.userMeta.roles.includes('admin') || user.userMeta.roles.includes('teacher')) {
        console.log('CourseEdit: User is admin/teacher - access granted to any course');
      } else if (data.instructor?._id?.toString() !== user._id?.toString()) {
        if (!data.isDraft) {
          toast.error('Вы не можете редактировать опубликованный курс');
          navigate(`/courses/${slug}`);
          return;
        }
      }

      // Дополнительная обработка модулей и загрузка тестов для уроков (параллельно)
      if (Array.isArray(data.modules) && data.modules.length > 0) {
        await Promise.all(
          data.modules.map(async (moduleRef) => {
            const module = moduleRef.module || moduleRef; // Поддержка старой структуры

            // Обрабатываем уроки модуля
            if (Array.isArray(module.lessons) && module.lessons.length > 0) {
              await Promise.all(
                module.lessons.map(async (lesson) => {
                  // Проверяем, есть ли тесты у урока
                  if (Array.isArray(lesson.tests) && lesson.tests.length > 0) {
                    const areTestsObjects = lesson.tests.some(
                      (test) => test && typeof test === 'object'
                    );

                    // Если тесты - это массив ID, загружаем их
                    if (!areTestsObjects) {
                      try {
                        const tests = await Promise.all(
                          lesson.tests.map(async (testId) => {
                            try {
                              const res = await testsAPI.getById(testId);
                              return res && res.data ? res.data : null;
                            } catch (err) {
                              console.error(`Ошибка при загрузке теста ${testId}:`, err);
                              return null;
                            }
                          })
                        );
                        lesson.tests = tests.filter(Boolean);
                      } catch (testsError) {
                        console.error(
                          `Ошибка при загрузке тестов для урока ${lesson._id}:`,
                          testsError
                        );
                        lesson.tests = [];
                      }
                    }
                  } else {
                    // Если тестов нет, инициализируем пустой массив
                    lesson.tests = [];
                  }
                })
              );
            }
          })
        );
      }

      // Устанавливаем данные курса, категория может быть строкой или объектом
      // Преобразуем профессии в массив ID, если они пришли как объекты
      const professionsIds = (data.professions || []).map(prof => 
        typeof prof === 'object' ? (prof.id || prof._id) : prof
      ).filter(Boolean);
      
      setCourseData({
        title: data.title,
        description: data.description,
        difficulty: data.difficulty,
        image: data.image,
        imagePreview: data.imagePreview,
        isDraft: data.isDraft,
        professions: professionsIds,
        tools: data.tools || [],
        modules: data.modules || [],
        videos: data.videos || [],
      });
      
      // Синхронизируем локальное состояние формы с загруженными данными
      setLocalFormData({
        title: data.title || '',
        description: data.description || '',
        difficulty: data.difficulty || 'beginner',
        image: data.image || '',
        videoUrl: data.videos && data.videos.length > 0 ? data.videos[0] : ''
      });
      
      setCourseId(data._id);
      // Videos are now loaded directly from course data

      setInitialLoad(false);
      setLoading(false);

      // Если у курса есть профессии, сохраняем их для последующего сопоставления
      // Полное сопоставление с данными из Redux будет выполнено в useEffect
      if (data.professions && data.professions.length > 0) {
        console.log('data.professions', data.professions);
        try {
          const courseProfessionIds = data.professions.map(prof => 
            String(typeof prof === 'object' ? (prof.id || prof._id) : prof)
          );
          
          // Сохраняем профессии временно (будут обновлены в useEffect после загрузки из Redux)
          setSelectedProfessions(data.professions);
          
          const lessonProfessionIds = new Set();
          (data.modules || []).forEach(moduleRef => {
            const module = moduleRef.module || moduleRef;
            if (module.lessons && Array.isArray(module.lessons)) {
              module.lessons.forEach(lesson => {
                if (lesson.professions && Array.isArray(lesson.professions)) {
                  lesson.professions.forEach(profId => {
                    const id = typeof profId === 'object' ? (profId.id || profId._id) : profId;
                    if (id) lessonProfessionIds.add(String(id));
                  });
                }
              });
            }
          });
          
          // Вручную добавленные = те что в курсе, но НЕТ в уроках
          const manualOnly = courseProfessionIds.filter(id => !lessonProfessionIds.has(id));
          setManualProfessions(new Set(manualOnly));
          // Не устанавливаем professionsInitialized здесь - это сделает useEffect после сопоставления
   
        } catch (error) {
          console.error('Error processing professions:', error);
        }
      }

      // Если у курса есть инструменты, загружаем их детали
      if (data.tools && data.tools.length > 0) {
        console.log('data.tools', data.tools);
        try {
          // Инструменты приходят как полные объекты
          setSelectedTools(data.tools);
        } catch (error) {
          console.error('Error processing tools:', error);
        }
      }

      // Set draft status
      setIsDraft(data.isDraft || false);
    } catch (error) {
      console.error('CourseEdit fetchData error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Ошибка при загрузке данных курса';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 403) {
          errorMessage = data.message || 'У вас нет доступа к этому курсу';
        } else if (status === 404) {
          errorMessage = 'Курс не найден';
        } else if (status === 401) {
          errorMessage = 'Необходима авторизация';
        } else {
          errorMessage = data.message || `Ошибка сервера (${status})`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Ошибка сети. Проверьте подключение к интернету';
      } else {
        // Other error
        errorMessage = error.message || 'Неизвестная ошибка';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  }, [slug, user, navigate]);

  // Загрузка данных курса
  useEffect(() => {
    if (slug && user) {
      fetchData();
    }
  }, [slug, fetchData, user]);

  // Загрузка профессий и инструментов из Redux при монтировании компонента
  useEffect(() => {
    dispatch(fetchProfessions({ all: true , isCommon:1 }));
    dispatch(fetchTools({ all: true, isCommon: 1 }));
    dispatch(fetchSkills({ all: true, isCommon: 1 }));
  }, [dispatch]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownOpen) {
        setStatusDropdownOpen(false);
      }
    };

    if (statusDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [statusDropdownOpen]);

  // Обработка событий удаления профессий из выбранных
  useEffect(() => {
    // Если профессия была удалена, нужно удалить её из выбранных
    const handleProfessionEvent = (action, data) => {
      if (action === 'delete' && data && data.id) {
        // Удаляем профессию из выбранных, если она была выбрана
        setSelectedProfessions(prevSelected => 
          prevSelected.filter(p => p.id !== data.id)
        );
        
        // Также обновляем данные курса, удаляя профессию из списка
        setCourseData(prevData => ({
          ...prevData,
          professions: prevData.professions.filter(p => p.id !== data.id)
        }));
      }
    };
    
    // Обработчик событий инструментов
    const handleToolEvent = (action, data) => {
      if (action === 'delete' && data && data.id) {
        // Удаляем инструмент из выбранных, если он был выбран
        setSelectedTools(prevSelected =>
          prevSelected.filter(t => t.id !== data.id)
        );

        // Также обновляем данные курса, удаляя инструмент из списка
        setCourseData(prevData => ({
          ...prevData,
          tools: prevData.tools.filter(t => t.id !== data.id)
        }));
      }
    };

    // Подписываемся на события профессий и инструментов
    const unsubscribeProfession = libraryEventsService.subscribe('profession', handleProfessionEvent);
    const unsubscribeTool = libraryEventsService.subscribe('tool', handleToolEvent);

    // Отписываемся при размонтировании компонента
    return () => {
      unsubscribeProfession();
      unsubscribeTool();
    };
  }, []);

  // Cleanup для debounce таймеров
  useEffect(() => {
    const refs = debounceRefs.current;
    
    return () => {
      // Очищаем все таймеры при размонтировании
      Object.values(refs).forEach(timer => {
        if (timer && typeof timer === 'number') {
          clearTimeout(timer);
        }
      });
      
      // Очищаем таймеры модулей
      if (refs.modules) {
        Object.values(refs.modules).forEach(moduleTimers => {
          if (moduleTimers && typeof moduleTimers === 'object') {
            Object.values(moduleTimers).forEach(timer => {
              if (timer && typeof timer === 'number') {
                clearTimeout(timer);
              }
            });
          }
        });
      }
    };
  }, []);

  // Универсальная функция для debounced обновления
  const createDebouncedUpdate = useCallback((updateFunction, delay = 300) => {
    return (fieldName, value, extraParams = {}) => {
      // Очищаем предыдущий таймер
      if (debounceRefs.current[fieldName]) {
        clearTimeout(debounceRefs.current[fieldName]);
      }

      // Устанавливаем новый таймер
      debounceRefs.current[fieldName] = setTimeout(() => {
        startTransition(() => {
        updateFunction(fieldName, value, extraParams);
        });
      }, delay);
    };
  }, []);

  // Функция для обновления основных полей курса
  const updateCourseField = useCallback((name, value) => {
    setCourseData(prevData => {
      // Особая обработка для videoUrl
      if (name === 'videoUrl') {
        return {
          ...prevData,
          videos: value ? [value] : [],
        };
      }
      
      return {
        ...prevData,
        [name]: value,
      };
    });
  }, []);

  // Функция для обновления полей модуля
  const updateModuleField = useCallback((fieldName, value, { moduleIndex }) => {
    setCourseData(prevData => {
      const updatedModules = (prevData.modules || []).map((moduleRef, idx) => {
        if (idx !== moduleIndex) return moduleRef;
        
        return {
          ...moduleRef,
          module: {
            ...moduleRef.module,
            [fieldName]: value,
          }
        };
      });
      
      return {
        ...prevData,
        modules: updatedModules,
      };
    });
  }, []);

  // Создаем debounced функции
  const debouncedCourseUpdate = createDebouncedUpdate(updateCourseField);

  // Обработчик для локального состояния с debouncing
  const handleLocalInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Обновляем локальное состояние немедленно для отзывчивого UI
    setLocalFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Запускаем debounced обновление основного состояния
    debouncedCourseUpdate(name, value);
  }, [debouncedCourseUpdate]);

  // Обработчик для полей модулей с debouncing
  const handleModuleInputChange = useCallback((moduleIndex, e) => {
    const { name, value } = e.target;
    const fieldKey = `module-${moduleIndex}-${name}`;
    
    // Обновляем локальное состояние модуля немедленно
    setLocalModuleFields(prev => ({
      ...prev,
      [fieldKey]: value
    }));

    // Очищаем предыдущий таймер для этого поля
    if (!debounceRefs.current.modules[moduleIndex]) {
      debounceRefs.current.modules[moduleIndex] = {};
    }
    
    if (debounceRefs.current.modules[moduleIndex][name]) {
      clearTimeout(debounceRefs.current.modules[moduleIndex][name]);
    }

    // Устанавливаем новый таймер
    debounceRefs.current.modules[moduleIndex][name] = setTimeout(() => {
      updateModuleField(name, value, { moduleIndex });
    }, 300);
  }, [updateModuleField]);

  // Обработчики изменения полей курса
  const handleCourseDataChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Для текстовых полей с debouncing используем локальное состояние
    if (['title', 'description', 'image', 'videoUrl'].includes(name)) {
      handleLocalInputChange(e);
      return;
    }

    // Для difficulty обновляем и локальное, и основное состояние
    if (name === 'difficulty') {
      setLocalFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Также обновляем основное состояние курса
      setCourseData(prevData => ({
        ...prevData,
        [name]: value,
      }));
    }

    // Для других полей обновляем напрямую
    setCourseData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  }, [handleLocalInputChange]);

  // Обработчик выбора профессий (для CustomAutocomplete без event)
  const handleProfessionsChange = useCallback((newValue) => {
    // Проверяем что newValue определен и является массивом
    if (!newValue || !Array.isArray(newValue)) {
      console.warn('handleProfessionsChange: newValue is not an array', newValue);
      return;
    }

    // Обновляем выбранные профессии
    setSelectedProfessions(newValue);

    // Обновляем данные курса, фильтруем только валидные ID
    const validProfessionIds = newValue
      .map((profession) => profession.id || profession._id)
      .filter(Boolean)
      .map(id => String(id));

    console.log('Выбраны профессии с ID:', validProfessionIds);

    // Определяем какие профессии из уроков
    const lessonProfessionIds = new Set();
    courseData.modules.forEach(moduleRef => {
      const module = moduleRef.module || moduleRef;
      if (module.lessons && Array.isArray(module.lessons)) {
        module.lessons.forEach(lesson => {
          if (lesson.professions && Array.isArray(lesson.professions)) {
            lesson.professions.forEach(profId => {
              const id = typeof profId === 'object' ? (profId.id || profId._id) : profId;
              if (id) lessonProfessionIds.add(String(id));
            });
          }
        });
      }
    });

    // Вручную добавленные = те что выбраны, но НЕТ в уроках
    const manualOnly = validProfessionIds.filter(id => !lessonProfessionIds.has(id));
    
    setManualProfessions(new Set(manualOnly));
    
    // Обновляем данные курса
    setCourseData((prevData) => ({
      ...prevData,
      professions: validProfessionIds,
    }));
  }, [courseData.modules]);

  // Функция для синхронизации профессий курса с профессиями уроков
  const syncProfessionsFromLessons = useCallback(() => {
    // Собираем все профессии из всех уроков всех модулей
    const allLessonProfessions = new Set();
    
    courseData.modules.forEach(moduleRef => {
      const module = moduleRef.module || moduleRef;
      if (module.lessons && Array.isArray(module.lessons)) {
        module.lessons.forEach(lesson => {
          if (lesson.professions && Array.isArray(lesson.professions)) {
            lesson.professions.forEach(profId => {
              // Добавляем ID профессии (может быть строкой или объектом)
              const id = typeof profId === 'object' ? (profId.id || profId._id) : profId;
              if (id) {
                allLessonProfessions.add(String(id));
              }
            });
          }
        });
      }
    });

    // Объединяем профессии из уроков с вручную добавленными профессиями
    const mergedProfessions = new Set([
      ...Array.from(manualProfessions), 
      ...Array.from(allLessonProfessions)
    ]);
    
    const mergedProfessionsArray = Array.from(mergedProfessions);
    const currentCourseProfessions = (courseData.professions || []).map(p => String(p));
    
    // Проверяем, изменились ли профессии
    const professionsChanged = 
      mergedProfessionsArray.length !== currentCourseProfessions.length ||
      !mergedProfessionsArray.every(id => currentCourseProfessions.includes(id)) ||
      !currentCourseProfessions.every(id => mergedProfessionsArray.includes(id));
    
    if (professionsChanged) {
  
      
      // Обновляем courseData
      setCourseData(prevData => ({
        ...prevData,
        professions: mergedProfessionsArray
      }));
      
      // Обновляем selectedProfessions - находим полные объекты профессий
      const updatedSelectedProfessions = professions.filter(prof => 
        mergedProfessionsArray.includes(String(prof.id || prof._id))
      );
      
      setSelectedProfessions(updatedSelectedProfessions);
    }
  }, [courseData.modules, courseData.professions, professions, manualProfessions]);

  // Сопоставление профессий из курса с данными из Redux после их загрузки
  useEffect(() => {
    // Если профессии загружены из Redux, курс загружен, но selectedProfessions еще не сопоставлены правильно
    if (professions.length > 0 && courseData.professions && courseData.professions.length > 0 && selectedProfessions.length > 0) {
      const courseProfessionIds = courseData.professions.map(prof => 
        String(typeof prof === 'object' ? (prof.id || prof._id) : prof)
      );
      
      // Проверяем, нужно ли обновить selectedProfessions (если они не содержат полных данных)
      const needsUpdate = selectedProfessions.some(prof => {
        const profId = String(prof.id || prof._id);
        const fullProf = professions.find(p => String(p.id || p._id) === profId);
        // Если в selectedProfessions нет полных данных (нет term_group), нужно обновить
        return !fullProf || (fullProf && !prof.term_group?.MainTerm?.value && fullProf.term_group?.MainTerm?.value);
      });
      
      if (needsUpdate) {
        // Сопоставляем ID профессий из курса с полными объектами из Redux
        const matchedProfessions = courseProfessionIds.map(profId => {
          return professions.find(prof => 
            String(prof.id || prof._id) === String(profId)
          );
        }).filter(Boolean);
        
        if (matchedProfessions.length > 0) {
          setSelectedProfessions(matchedProfessions);
          if (!professionsInitialized) {
            setProfessionsInitialized(true);
          }
        }
      } else if (!professionsInitialized) {
        // Если обновление не нужно, но флаг не установлен, устанавливаем его
        setProfessionsInitialized(true);
      }
    }
  }, [professions, courseData.professions, selectedProfessions, professionsInitialized]);

  // Синхронизация профессий после загрузки курса и профессий
  useEffect(() => {
    // Вызываем синхронизацию только если:
    // 1. Курс загружен (courseData.modules существует)
    // 2. Профессии загружены из Redux
    // 3. Это не начальная загрузка
    // 4. Профессии уже инициализированы (чтобы не перезаписывать при первой загрузке)
    if (!initialLoad && courseData.modules && courseData.modules.length > 0 && professions.length > 0 && professionsInitialized) {
      syncProfessionsFromLessons();
    }
  }, [initialLoad, courseData.modules, professions, syncProfessionsFromLessons, professionsInitialized]);

  // Обработчик выбора инструментов
  const handleToolsChange = useCallback((eventOrValue, maybeValue) => {
    // Определяем, передан ли event или значение напрямую
    // Если первый параметр - массив, значит это значение (из CustomAutocomplete)
    // Если второй параметр определен, значит первый - event (из MUI Autocomplete)
    const newValue = Array.isArray(eventOrValue) ? eventOrValue : maybeValue;

    // Проверяем что newValue определен и является массивом
    if (!newValue || !Array.isArray(newValue)) {
      console.warn('handleToolsChange: newValue is not an array', newValue);
      return;
    }

    // Обновляем выбранные инструменты
    setSelectedTools(newValue);

    // Обновляем данные курса, фильтруем только валидные ID
    const validToolIds = newValue
      .filter((tool) => tool && tool.id)
      .map((tool) => tool.id);

    console.log('Выбраны инструменты с ID:', validToolIds);

    setCourseData((prevData) => ({
      ...prevData,
      tools: validToolIds,
    }));
  }, []);



  const addModule = useCallback(() => {
    setCourseData(prevData => {
      const newModuleIndex = prevData.modules.length;
      
      // Добавляем локальные состояния для нового модуля
      setLocalModuleFields(prev => ({
        ...prev,
        [`module-${newModuleIndex}-title`]: '',
        [`module-${newModuleIndex}-description`]: '',
        [`module-${newModuleIndex}-content`]: '',
      }));

      return {
        ...prevData,
        modules: [
          ...prevData.modules,
          {
            title: '',
            description: '',
            content: '',
            lessons: [
              {
                title: '',
                content: '',
                duration: 15,
                type: 'text',
                contentType: 'standard',
                resources: [],
                practiceExercises: [],
                contentBlocks: [],
                professions: [],
                tests: [], // Тесты теперь находятся внутри урока
              },
            ],
          },
        ],
      };
    });
  }, []);

  // Функция для обработки выбора существующих модулей
  const handleSelectExistingModules = useCallback(async (selectedModuleIds) => {
    try {
      // Загружаем данные выбранных модулей
      const modulePromises = selectedModuleIds.map(moduleId =>
        modulesAPI.getById(moduleId)
      );
      const moduleResponses = await Promise.all(modulePromises);
      const selectedModules = moduleResponses.map(response => response.data);

      // Добавляем модули в курс с правильной структурой
      setCourseData(prevData => {
        const newModules = selectedModules.map((module, index) => {
          // Обрабатываем уроки модуля
          const lessons = (module.lessons || []).map(lesson => {
            // Если lesson - это объект с данными урока
            if (typeof lesson === 'object' && lesson._id) {
              return {
                _id: lesson._id,
                title: lesson.title || '',
                description: lesson.description || '',
                content: lesson.content || '',
                duration: lesson.duration || 15,
                type: lesson.type || 'text',
                contentType: lesson.contentType || 'standard',
                contentBlocks: lesson.contentBlocks || [],
                resources: lesson.resources || [],
                practiceExercises: lesson.practiceExercises || [],
                professions: lesson.professions || [],
                skills: lesson.skills || [],
                videoUrl: lesson.videoUrl || '',
                image: lesson.image || '',
                isDraft: lesson.isDraft || false,
                tests: lesson.tests || []
              };
            }
            // Если lesson - это только ID, возвращаем базовую структуру
            return {
              _id: lesson,
              title: '',
              content: '',
              duration: 15,
              type: 'text',
              contentType: 'standard',
              contentBlocks: [],
              resources: [],
              practiceExercises: [],
              professions: [],
              skills: [],
              videoUrl: '',
              image: '',
              isDraft: false,
              tests: []
            };
          });

          // ВАЖНО: Для CourseEdit модули должны иметь структуру moduleRef с вложенным module
          return {
            module: {
              _id: module._id, // Обязательно сохраняем ID существующего модуля!
              title: module.title || '',
              description: module.description || '',
              content: module.content || '',
              videoUrl: module.videoUrl || '',
              slug: module.slug || '', // Также сохраняем slug
              previewImage: module.previewImage || '', // Сохраняем превью изображение
              lessons: lessons,
              order: prevData.modules.length + index,
              isDraft: module.isDraft || false
            }
          };
        });

        // Инициализируем локальные поля для новых модулей
        const newLocalFields = {};
        newModules.forEach((moduleRef, index) => {
          const moduleIndex = prevData.modules.length + index;
          const module = moduleRef.module;
          newLocalFields[`module-${moduleIndex}-title`] = module.title || '';
          newLocalFields[`module-${moduleIndex}-description`] = module.description || '';
          newLocalFields[`module-${moduleIndex}-content`] = module.content || '';
        });

        setLocalModuleFields(prev => ({
          ...prev,
          ...newLocalFields
        }));

        return {
          ...prevData,
          modules: [
            ...prevData.modules,
            ...newModules
          ]
        };
      });

      toast.success(`Добавлено ${selectedModules.length} модулей в курс`);
    } catch (error) {
      console.error('Ошибка при добавлении модулей:', error);
      toast.error('Ошибка при добавлении модулей');
    }
  }, []);

  // Обработчик открытия модального окна подтверждения удаления модуля
  const handleOpenDeleteModuleDialog = useCallback((moduleIndex) => {
    const moduleRef = courseData.modules[moduleIndex];
    if (!moduleRef) return;

    const module = moduleRef.module || moduleRef;

    setDeleteModuleDialog({
      open: true,
      moduleIndex,
      moduleId: module._id || null,
      moduleTitle: module.title || t('courses:courseCreate.ui.module_new'),
      loading: false
    });
  }, [courseData.modules, t]);

  // Обработчик закрытия модального окна подтверждения удаления модуля
  const handleCloseDeleteModuleDialog = useCallback(() => {
    setDeleteModuleDialog({
      open: false,
      moduleIndex: null,
      moduleId: null,
      moduleTitle: '',
      loading: false
    });
  }, []);

  // Функция для удаления модуля из локального состояния (вызывается после успешного удаления через API)
  const removeModule = useCallback((index) => {
    // Удаляем локальные состояния для удаляемого модуля
    setLocalModuleFields(prev => {
      const updated = { ...prev };
      delete updated[`module-${index}-title`];
      delete updated[`module-${index}-description`];
      delete updated[`module-${index}-content`];

      // Перенумеровываем локальные состояния для модулей, которые идут после удаляемого
      const reindexed = {};
      Object.keys(updated).forEach(key => {
        const match = key.match(/^module-(\d+)-(.+)$/);
        if (match) {
          const moduleIndex = parseInt(match[1]);
          const fieldName = match[2];

          if (moduleIndex > index) {
            // Сдвигаем индекс на единицу назад
            reindexed[`module-${moduleIndex - 1}-${fieldName}`] = updated[key];
          } else {
            reindexed[key] = updated[key];
          }
        } else {
          reindexed[key] = updated[key];
        }
      });

      return reindexed;
    });

    // Обновляем состояние раскрытых модулей
    setExpandedModules(prev => {
      const updated = { ...prev };
      delete updated[index];

      // Перенумеровываем индексы для модулей, которые идут после удаляемого
      const reindexed = {};
      Object.keys(updated).forEach(key => {
        const moduleIndex = parseInt(key);
        if (moduleIndex > index) {
          reindexed[moduleIndex - 1] = updated[key];
        } else {
          reindexed[key] = updated[key];
        }
      });

      return reindexed;
    });

    setCourseData(prevData => {
      const updatedModules = [...prevData.modules];
      updatedModules.splice(index, 1);
      return {
        ...prevData,
        modules: updatedModules,
      };
    });
  }, []);

  // Обработчик подтверждения удаления модуля
  const handleConfirmDeleteModule = useCallback(async () => {
    const { moduleIndex, moduleId } = deleteModuleDialog;

    if (moduleIndex === null) return;

    // Устанавливаем состояние загрузки
    setDeleteModuleDialog(prev => ({ ...prev, loading: true }));

    try {
      // Если модуль существует на сервере и привязан к курсу, удаляем его через API
      if (moduleId && courseId) {
        await modulesAPI.removeFromCourse(moduleId, courseId);
        toast.success(t('courses:courseCreate.toasts.moduleRemovedFromCourse'));
      }

      // Удаляем модуль из локального состояния
      removeModule(moduleIndex);

      // Закрываем модальное окно
      handleCloseDeleteModuleDialog();
    } catch (error) {
      console.error('Ошибка при удалении модуля из курса:', error);
      toast.error(t('courses:courseCreate.toasts.moduleRemoveError'));

      // Сбрасываем состояние загрузки
      setDeleteModuleDialog(prev => ({ ...prev, loading: false }));
    }
  }, [deleteModuleDialog, courseId, handleCloseDeleteModuleDialog, removeModule, t]);

  const addLesson = useCallback((moduleIndex) => {
    setCourseData(prevData => {
      const updatedModules = [...(prevData.modules || [])];
      const moduleRef = updatedModules[moduleIndex];

      if (!moduleRef) return prevData;

      const module = moduleRef.module || moduleRef;

      // Модули всегда имеют вложенную структуру moduleRef.module
      updatedModules[moduleIndex] = {
        ...moduleRef,
        module: {
          ...module,
          lessons: [
            ...(module.lessons || []),
            {
              title: '',
              content: '',
              duration: 15,
              type: 'text',
              contentType: 'standard',
              resources: [],
              practiceExercises: [],
              contentBlocks: [],
              professions: [],
              tests: [], // Тесты теперь находятся внутри урока
            }
          ]
        }
      };
      
      return {
        ...prevData,
        modules: updatedModules,
      };
    });
  }, []);

  // Открытие селектора уроков (мемоизировано)
  const handleOpenLessonSelectorMemo = useCallback((moduleIndex) => {
    setCurrentModuleIndex(moduleIndex);
    setLessonSelectorOpen(true);
  }, []);

  // Обработчик для управления раскрытием аккордеонов модулей
  const handleToggleExpanded = useCallback((moduleIndex, isExpanded) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleIndex]: isExpanded
    }));
  }, []);

  // Обработчик изменений в уроках модуля
  const handleLessonChange = useCallback((moduleIndex, action, data, lessonIndex = null) => {
    setCourseData(prev => {
      const updatedModules = [...prev.modules];
      const moduleRef = updatedModules[moduleIndex];
      const currentLessons = (moduleRef.module?.lessons || moduleRef.lessons || []).slice();
      
      switch (action) {
        case 'add':
          currentLessons.push(data);
          break;
        case 'addArray':
          currentLessons.push(...data);
          break;
        case 'update':
          if (lessonIndex !== null) {
            currentLessons[lessonIndex] = data;
          }
          break;
        case 'remove':
          if (lessonIndex !== null) {
            const lessonToRemove = currentLessons[lessonIndex];
            
            // Если урок имеет ID (существует на сервере), сохраняем информацию для удаления
            if (lessonToRemove && lessonToRemove._id) {
              const module = moduleRef.module || moduleRef;
              const moduleId = module._id;
              
              if (moduleId) {
                // Добавляем в Map: lessonId -> moduleId
                setDeletedLessonsMap(prev => {
                  const newMap = new Map(prev);
                  newMap.set(lessonToRemove._id, moduleId);
                  return newMap;
                });
              }
            }
            
            currentLessons.splice(lessonIndex, 1);
          }
          break;
        default:
          break;
      }
      
      if (moduleRef.module) {
        updatedModules[moduleIndex] = {
          ...moduleRef,
          module: { ...moduleRef.module, lessons: currentLessons },
        };
      } else {
        updatedModules[moduleIndex] = { ...moduleRef, lessons: currentLessons };
      }
      
      return { ...prev, modules: updatedModules };
    });
    
    // Синхронизируем профессии после изменения урока
    setTimeout(() => syncProfessionsFromLessons(), 0);
  }, [syncProfessionsFromLessons]);


  // Обработчик изменения порядка модуля
  const handleModuleOrderChange = useCallback((moduleIndex, newOrder) => {
    updateModuleField('order', newOrder, { moduleIndex });
  }, [updateModuleField]);

  // Обработчики для превью HeroSection
  const formatDuration = useCallback((minutes, translationFn) => {
    if (!minutes) return 'N/A';

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} ${translationFn('common:time.shortMin')}`;
    } else if (remainingMinutes === 0) {
      return `${hours} ${translationFn('common:time.shortHour')}`;
    } else {
      return `${hours} ${translationFn('common:time.shortHour')} ${remainingMinutes} ${translationFn('common:time.shortMin')}`;
    }
  }, []);

  const handleHeroPreviewToggleBookmark = useCallback(() => {
    // Ничего не делаем в превью
  }, []);

  const handleHeroPreviewContinueLearning = useCallback(() => {
    // Ничего не делаем в превью
  }, []);

  const handleHeroPreviewEnroll = useCallback(() => {
    // Ничего не делаем в превью
  }, []);

  const handleHeroPreviewScrollToReviews = useCallback(() => {
    // Ничего не делаем в превью
  }, []);

  // Функция для вычисления статистики курса для превью
  const calculatePreviewStats = useCallback(() => {
    let totalDuration = 0;
    let lessonCount = 0;
    let testCount = 0;
    let presentationCount = 0;
    const videoContent = [];

    courseData.modules.forEach(module => {
      const moduleActual = module.module || module;
      if (moduleActual.lessons && Array.isArray(moduleActual.lessons)) {
        moduleActual.lessons.forEach(lesson => {
          lessonCount++;
          totalDuration += lesson.duration || 15;
          
          // Проверяем наличие видео
          if (lesson.videoUrl) {
            videoContent.push(lesson.videoUrl);
          }
          
          // Проверяем тип контента (presentation)
          if (lesson.contentType === 'presentation') {
            presentationCount++;
          }
          
          // Считаем тесты в уроке
          if (lesson.tests && Array.isArray(lesson.tests)) {
            testCount += lesson.tests.length;
          }
        });
      }
    });

    // Проверяем основное видео курса
    if (courseData.videos && courseData.videos.length > 0) {
      videoContent.push(courseData.videos[0]);
    }

    return {
      duration: totalDuration,
      lessonCount,
      testCount,
      videoContent,
      presentationCount,
      totalStudents: 0, // Для превью всегда 0
    };
  }, [courseData.modules, courseData.videos]);

  // Подготовка данных для HeroSection превью
  const getPreviewCourseData = useCallback(() => {
    // Приоритет: imagePreview (data URL) > image (URL или путь)
    let imageUrl = '';
    
    if (courseData.imagePreview) {
      // Используем preview изображение (data URL)
      imageUrl = courseData.imagePreview;
    } else if (courseData.image) {
      // Используем основное изображение
      imageUrl = courseData.image;
    }
    
    const previewData = {
      title: courseData.title || 'Course Title',
      description: courseData.description || 'Course description...',
      difficulty: courseData.difficulty || 'beginner',
      image: imageUrl,
      isDraft: isDraft,
      professions: selectedProfessions,
      tools: selectedTools,
      category: courseData.category || { slug: 'default' },
    };
    
    return previewData;
  }, [courseData, isDraft, selectedProfessions, selectedTools]);

  // Мемоизированный компонент секции модулей для снижения количества ререндеров
  const ModulesSectionMemo = useMemo(() => (
    <Grid item xs={12}>
      {/* Hero Preview (if open) - Full Width, НАД модулями */}
      {heroPreviewOpen && (
        <Box sx={{ 
          width: '100%', 
          mb: 4,
          borderRadius: 3,
          overflow: 'hidden',
          pointerEvents: 'none'
        }}>
          <HeroSection
            course={getPreviewCourseData()}
            courseStats={calculatePreviewStats()}
            isEnrolled={false}
            isBookmarked={false}
            onToggleBookmark={handleHeroPreviewToggleBookmark}
            onContinueLearning={handleHeroPreviewContinueLearning}
            onEnroll={handleHeroPreviewEnroll}
            formatDuration={formatDuration}
            onScrollToReviews={handleHeroPreviewScrollToReviews}
            userRating={0}
            onRatingSubmit={() => {}}
          />
        </Box>
      )}

      {/* Header with title and buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h5" component="h2">
          {t('courses:courseCreate.ui.modulesAndLessons')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<LibraryAddIcon />}
            onClick={() => setModuleSelectorOpen(true)}
            color="secondary"
          >
            {t('courses:courseCreate.ui.addExistingModule')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addModule}
          >
            {t('courses:courseCreate.ui.addModule')}
          </Button>
        </Box>
      </Box>

      {courseData.modules?.map((moduleRef, moduleIndex) => {
        const module = moduleRef.module || moduleRef;
        const moduleKey = module._id || `module-${moduleIndex}`;
        
        return (
          <ModuleCard
            key={moduleKey}
            moduleRef={moduleRef}
            moduleIndex={moduleIndex}
            totalModules={courseData.modules.length}
            isExpanded={expandedModules[moduleIndex] || false}
            localModuleFields={localModuleFields}
            user={user}
            isDraft={(moduleRef.module || moduleRef).isDraft || false}
            showOrderField={true}
            onToggleExpanded={handleToggleExpanded}
            onModuleFieldChange={handleModuleInputChange}
            onRemoveModule={handleOpenDeleteModuleDialog}
            onAddLesson={addLesson}
            onOpenLessonSelector={handleOpenLessonSelectorMemo}
            onLessonChange={handleLessonChange}
            onModuleOrderChange={handleModuleOrderChange}
            onProfessionsSync={syncProfessionsFromLessons}
            onDraftToggle={(moduleIndex, isDraft) => {
              const updatedModules = (courseData.modules || []).map((mod, idx) => {
                if (idx === moduleIndex) {
                  const module = mod.module || mod;
                  return {
                    ...mod,
                    module: { ...module, isDraft }
                  };
                }
                return mod;
              });
              setCourseData(prev => ({ ...prev, modules: updatedModules }));
            }}
          />
        );
      })}
    </Grid>
  ), [
    courseData.modules,
    expandedModules,
    localModuleFields,
    t,
    user,
    handleToggleExpanded,
    handleModuleInputChange,
    handleOpenDeleteModuleDialog,
    addLesson,
    addModule,
    handleOpenLessonSelectorMemo,
    handleLessonChange,
    handleModuleOrderChange,
    syncProfessionsFromLessons,
    heroPreviewOpen,
    getPreviewCourseData,
    calculatePreviewStats,
    handleHeroPreviewToggleBookmark,
    handleHeroPreviewContinueLearning,
    handleHeroPreviewEnroll,
    handleHeroPreviewScrollToReviews,
    formatDuration
  ]);

  // Проверка прав доступа
  if (!user) {
    return (
      <Container>
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{t('courses:courseCreate.ui.noAccess')}</Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/courses')}
            sx={{ mt: 2 }}
          >
            {t('courses:courseCreate.ui.backToCourses')}
          </Button>
        </Box>
      </Container>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      // Validation
      if (!courseData.title || !courseData.description) {
        setError('Заполните все обязательные поля');
        setSubmitting(false);
        return;
      }

      // Удаляем уроки из модулей (отвязываем их)
      if (deletedLessonsMap.size > 0) {
        for (const [lessonId, moduleId] of deletedLessonsMap) {
          try {
            await modulesAPI.removeLessonFromModule(moduleId, lessonId);
          } catch (error) {
            // Не прерываем процесс, продолжаем с другими уроками
          }
        }
        
        // Очищаем Map после удаления
        setDeletedLessonsMap(new Map());
      }

      // Подготовка данных курса для отправки
      const preparedCourseData = {
        ...courseData,
        professions: selectedProfessions.map((prof) => prof.id),
        tools: selectedTools.map((tool) => tool.id),
        videos: courseData.videos || [], // Include videos array
        modules: [], // Убираем модули, они будут отправлены отдельно
      };

      // Подготовка данных модулей для отдельной отправки
      const preparedModules = (courseData.modules || []).map((moduleRef) => {
        // Извлекаем данные модуля из правильной структуры
        const module = moduleRef.module || moduleRef;

        // Создаем минимальный объект модуля только с необходимыми полями для обновления
        const preparedModule = {
          title: module.title || '',
          description: module.description || '',
          content: module.content || '',
          videoUrl: module.videoUrl || '',
          isDraft: module.isDraft === undefined ? false : module.isDraft,
          courseId: courseId  // Добавляем courseId для привязки модуля к курсу
        };

        // Добавляем дополнительные поля только если они есть и не пустые
        if (module.slug) {
          preparedModule.slug = module.slug;
        }
        if (module.order !== undefined) {
          preparedModule.order = module.order;
        }
        if (module.duration !== undefined) {
          preparedModule.duration = module.duration;
        }
        if (module.difficulty) {
          preparedModule.difficulty = module.difficulty;
        }

        // Добавляем ID только если он существует и не является временным
        if (module._id &&
            typeof module._id === 'string' &&
            !module._id.startsWith('temp-') &&
            module._id.length === 24) {
          preparedModule._id = module._id;
        }

        return preparedModule;
      });

      // Удаляем поле imagePreview, оно нужно только для UI
      delete preparedCourseData.imagePreview;

      // Проверяем, загружено ли новое изображение (data URL)
      const isDataURL = courseData.imagePreview && courseData.imagePreview.startsWith('data:');

      console.log('Отправка данных курса');

      if (isDataURL) {
        // Если есть загруженное изображение, используем FormData
        const formData = new FormData();

        // Преобразуем data URL в Blob
        const imageBlob = await fetch(courseData.imagePreview).then((r) =>
          r.blob()
        );
        formData.append('image', imageBlob, 'course-image.jpg');

        // Удаляем поле image, оно не нужно для отправки
        delete preparedCourseData.image;
        // Добавляем JSON с данными курса
        formData.append('data', JSON.stringify(preparedCourseData));

        // Отправляем с правильными заголовками
        await coursesAPI.updateWithImage(courseId, formData);
      } else {
        // Если изображения нет или это URL, отправляем обычным JSON
        await coursesAPI.update(courseId, preparedCourseData);
      }


      // 2. ПОСЛЕДОВАТЕЛЬНОЕ обновление: модуль → уроки → тесты → следующий модуль
      console.log(`Начинаем обновление ${preparedModules.length} модулей последовательно...`);

      // Счетчики для отслеживания успехов и ошибок
      let totalErrors = 0;
      let totalWarnings = 0;

      for (let moduleIndex = 0; moduleIndex < preparedModules.length; moduleIndex++) {
        const moduleToUpdate = preparedModules[moduleIndex];
        const originalModuleRef = courseData.modules[moduleIndex];
        const originalModule = originalModuleRef.module || originalModuleRef;

        console.log(`\n📦 Модуль ${moduleIndex + 1}/${preparedModules.length}: "${moduleToUpdate.title}"`);

        // 2.1. Создаем или обновляем модуль через modulesAPI
        let moduleId;
        try {
          let moduleResponse;

          // Проверяем, есть ли файл превью изображения для загрузки
          const hasPreviewImageFile = originalModule.previewImageFile instanceof File;

          // Проверяем, существует ли модуль
          if (originalModule._id && typeof originalModule._id === 'string' && originalModule._id.length === 24) {
            // Обновляем существующий модуль
            moduleId = originalModule._id;

            if (hasPreviewImageFile) {
              // Обновляем с изображением через FormData
              const formData = new FormData();
              formData.append('previewImage', originalModule.previewImageFile);
              Object.keys(moduleToUpdate).forEach(key => {
                formData.append(key, moduleToUpdate[key]);
              });
              moduleResponse = await modulesAPI.updateWithImage(moduleId, formData);
            } else {
              // Обновляем без изображения
              moduleResponse = await modulesAPI.update(moduleId, moduleToUpdate);
            }
            console.log(`  ✓ Модуль обновлен с ID: ${moduleId}`);
          } else {
            // Создаем новый модуль
            if (hasPreviewImageFile) {
              // Создаем с изображением через FormData
              const formData = new FormData();
              formData.append('previewImage', originalModule.previewImageFile);
              Object.keys(moduleToUpdate).forEach(key => {
                formData.append(key, moduleToUpdate[key]);
              });
              moduleResponse = await modulesAPI.createWithImage(formData);
            } else {
              // Создаем без изображения
              moduleResponse = await modulesAPI.create(moduleToUpdate);
            }
            moduleId = moduleResponse.data.module._id;

            if (!moduleId) {
              throw new Error('Не получен ID созданного модуля');
            }

            console.log(`  ✓ Модуль создан и привязан к курсу с ID: ${moduleId}`);
          }
        } catch (error) {
          console.error(`  ✗ Ошибка при создании/обновлении модуля "${moduleToUpdate.title}":`, error);
          toast.error(`Ошибка при обновлении модуля "${moduleToUpdate.title}"`);
          totalErrors++;
          continue; // Переходим к следующему модулю
        }

        // 2.2. Для этого модуля обновляем уроки последовательно
        const moduleLessons = originalModule.lessons || [];
        console.log(`  📚 Обновляем ${moduleLessons.length} уроков для модуля...`);

        const results = [];
        const errors = [];

        for (let lessonIndex = 0; lessonIndex < moduleLessons.length; lessonIndex++) {
          const originalLesson = moduleLessons[lessonIndex];

          console.log(`    📖 Урок ${lessonIndex + 1}/${moduleLessons.length}: "${originalLesson.title}"`);

          // Создаем базовую структуру урока
          const lessonData = {
            title: originalLesson.title,
            description: originalLesson.description || '',
            content: originalLesson.content,
            duration: Number(originalLesson.duration) || 15,
            type: originalLesson.type || 'text',
            contentType: originalLesson.contentType || 'standard',
            contentBlocks: originalLesson.contentBlocks || [],
            resources: originalLesson.resources || [],
            practiceExercises: originalLesson.practiceExercises || [],
            professions: originalLesson.professions || [],
            image: originalLesson.image || '',
            videoUrl: originalLesson.videoUrl || '',
            isDraft: originalLesson.isDraft === undefined ? false : originalLesson.isDraft,
            moduleId: moduleId,
            courseId: courseId
          };

          // Если урок имеет _id и это не временный ID, сохраняем его
          if (originalLesson._id &&
              typeof originalLesson._id === 'string' &&
              !originalLesson._id.startsWith('temp-')) {
            lessonData._id = originalLesson._id;
          }

          let lessonId;
          try {
            const hasImageToUpload = lessonData.imageFile || (lessonData.imagePreview && !lessonData.image);

            let response;

            // Если урок существующий, обновляем его
            if (originalLesson._id && typeof originalLesson._id === 'string' && !originalLesson._id.startsWith('temp-')) {
              lessonId = originalLesson._id;
              if (hasImageToUpload) {
                // Обновляем урок с изображением
                const formData = new FormData();

                const lessonDataForUpload = {
                  title: lessonData.title || '',
                  content: lessonData.content || '',
                  duration: Number(lessonData.duration) || 0,
                  type: lessonData.type || 'text',
                  contentType: lessonData.contentType || 'standard',
                  videoUrl: lessonData.videoUrl || '',
                  isDraft: lessonData.isDraft || false,
                  contentBlocks: lessonData.contentBlocks || [],
                  resources: lessonData.resources || [],
                  practiceExercises: lessonData.practiceExercises || [],
                  professions: lessonData.professions || [],
                  moduleId: moduleId,  // Привязка к модулю через moduleId
                  courseId: courseId
                };

                formData.append('data', JSON.stringify(lessonDataForUpload));

                // Добавляем изображение
                if (lessonData.imageFile) {
                  formData.append('image', lessonData.imageFile);
                } else if (lessonData.imagePreview) {
                  const imageBlob = await fetch(lessonData.imagePreview).then(r => r.blob());
                  formData.append('image', imageBlob, 'lesson-image.jpg');
                }
                response = await lessonsAPI.updateWithImage(lessonId, formData);
              } else {
                // Обновляем урок без изображения
                response = await lessonsAPI.update(lessonId, lessonData);
              }

              console.log(`      ✓ Урок обновлен с ID: ${lessonId}`);
            } else {
              // Создаем новый урок
              if (hasImageToUpload) {
                // Создаем урок с изображением
                const formData = new FormData();

                const lessonDataForUpload = {
                  title: lessonData.title || '',
                  content: lessonData.content || '',
                  duration: Number(lessonData.duration) || 0,
                  type: lessonData.type || 'text',
                  contentType: lessonData.contentType || 'standard',
                  videoUrl: lessonData.videoUrl || '',
                  isDraft: lessonData.isDraft || false,
                  contentBlocks: lessonData.contentBlocks || [],
                  resources: lessonData.resources || [],
                  practiceExercises: lessonData.practiceExercises || [],
                  professions: lessonData.professions || [],
                  moduleId: moduleId,  // Привязка к модулю через moduleId
                  courseId: courseId
                };

                formData.append('data', JSON.stringify(lessonDataForUpload));

                // Добавляем изображение
                if (lessonData.imageFile) {
                  formData.append('image', lessonData.imageFile);
                } else if (lessonData.imagePreview) {
                  const imageBlob = await fetch(lessonData.imagePreview).then(r => r.blob());
                  formData.append('image', imageBlob, 'lesson-image.jpg');
                }

                response = await lessonsAPI.createWithImage(formData);
              } else {
                // Создаем урок без изображения
                response = await lessonsAPI.create(lessonData);
              }

              // Пробуем разные варианты получения ID из ответа
              lessonId = response.data.data?._id || response.data._id || response.data.lesson?._id || response.data.data?.id;

              if (!lessonId) {
                console.error('Структура ответа сервера при создании урока:', response.data);
                throw new Error('Не получен ID созданного урока из ответа сервера');
              }

              console.log(`      ✓ Урок создан с ID: ${lessonId}`);
            }
            results.push({
              index: lessonIndex,
              success: true,
              data: response.data,
              lesson: originalLesson
            });

            // Обновляем состояние урока в courseData после успешного сохранения
            const updatedModules = [...courseData.modules];
            const moduleRef = updatedModules[moduleIndex];
            const actualModule = moduleRef.module || moduleRef;

            if (actualModule.lessons && actualModule.lessons[lessonIndex]) {
              actualModule.lessons[lessonIndex] = {
                ...actualModule.lessons[lessonIndex],
                ...response.data,
                imagePreview: null,
                imageFile: null
              };

              setCourseData(prev => ({
                ...prev,
                modules: updatedModules
              }));
            }
          } catch (error) {
            console.error(`      ✗ Ошибка при создании/обновлении урока "${originalLesson.title}":`, error);
            toast.error(`Ошибка при создании/обновлении урока "${originalLesson.title}"`);
            totalErrors++;
            errors.push({
              index: lessonIndex,
              error: error.message || 'Неизвестная ошибка',
              lesson: originalLesson
            });
            continue; // Переходим к следующему уроку
          }

          // 2.3. Для этого урока обновляем тесты последовательно
          const lessonTests = originalLesson.tests || [];

          if (lessonTests.length > 0) {
            console.log(`      🧪 Обновляем ${lessonTests.length} тестов для урока...`);

            for (let testIndex = 0; testIndex < lessonTests.length; testIndex++) {
              const test = lessonTests[testIndex];

              console.log(`        📝 Тест ${testIndex + 1}/${lessonTests.length}: "${test.title}"`);

              const testData = {
                ...(test._id && !test._id.startsWith('temp-test') && { _id: test._id }),
                lessonId: lessonId,
                title: test.title,
                description: test.description || '',
                timeLimit: Number(test.timeLimit) || 15,
                passingScore: Number(test.passingScore) || 70,
                attempts: Number(test.attempts) || 3,
                questions: test.questions.map((q) => ({
                  ...q,
                  points: Number(q.points) || 1,
                  type: q.type || 'single-choice',
                })),
              };

              try {
                if (testData._id) {
                  await testsAPI.update(testData._id, testData);
                  console.log(`          ✓ Тест обновлен`);
                } else {
                  await testsAPI.create(testData);
                  console.log(`          ✓ Тест создан`);
                }
              } catch (error) {
                console.error(`          ✗ Ошибка при обновлении теста "${test.title}":`, error);
                totalWarnings++;  // Тест не критичен, считаем как предупреждение
                // Не показываем toast для каждого теста, чтобы не спамить
                // Продолжаем со следующим тестом
              }
            }
          }
        }

        if (errors.length > 0) {
          console.warn(`  ⚠️ Некоторые уроки модуля "${moduleToUpdate.title}" не удалось обработать:`, errors);
        } else {
          console.log(`  ✓ Модуль "${moduleToUpdate.title}" и его содержимое успешно обновлены\n`);
        }
      }

      // Показываем итоговое сообщение в зависимости от результатов
      if (totalErrors > 0) {
        console.log(`⚠️ Обновление завершено с ошибками: ${totalErrors} критических ошибок`);
        setSuccess('Курс обновлен с ошибками');
        toast.warning(`Курс обновлен, но с ${totalErrors} ошибками. Проверьте консоль для деталей.`);
      } else if (totalWarnings > 0) {
        console.log(`⚠️ Обновление завершено с предупреждениями: ${totalWarnings} тестов не обновлены`);
        setSuccess('Курс обновлен с предупреждениями');
        toast.warning(`Курс обновлен, но ${totalWarnings} тестов не удалось обновить. Вы можете исправить их позже.`);
      } else {
        console.log('✅ Все модули, уроки и тесты успешно обновлены!');
        setSuccess('Курс успешно обновлен');
        toast.success('Курс успешно обновлен');
      }

      // Не перезагружаем данные с сервера, так как локальное состояние уже обновлено
      // await fetchData();

      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
      setError(`Ошибка при обновлении курса: ${error.message}`);
      console.error('Ошибка при обновлении курса:', error);
      toast.error(`Ошибка при обновлении курса: ${error.message}`);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataUrl = e.target.result;
        setCourseData({
          ...courseData,
          image: imageDataUrl,
          imagePreview: imageDataUrl,
        });
        // Обновляем локальное состояние
        setLocalFormData(prev => ({
          ...prev,
          image: imageDataUrl
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setCourseData({
      ...courseData,
      image: '',
      imagePreview: '',
    });
    // Обновляем локальное состояние
    setLocalFormData(prev => ({
      ...prev,
      image: ''
    }));
  };

  const handleVideoUrlChange = (e) => {
    const value = e.target.value;
    handleLocalInputChange({ target: { name: 'videoUrl', value } });
  };




  // Обработчик импорта JSON
  const handleJsonImport = async (jsonData) => {
    try {
      // Проверяем, что jsonData содержит нужные поля
      if (!jsonData.title || !jsonData.description) {
        toast.error(
          t('courses:courseCreate.toasts.jsonMissingFields')
        );
        return;
      }

      // Если импортируем полный курс с course-объектом
      if (jsonData.course) {
        setCourseData({
          ...courseData,
          title: jsonData.course.title,
          description: jsonData.course.description,
          difficulty: jsonData.course.difficulty,
          modules: jsonData.course.modules || [],
        });
      } else {
        // Иначе просто импортируем данные как есть
        setCourseData({
          ...courseData,
          ...jsonData,
        });
      }

      toast.success(t('courses:courseCreate.toasts.jsonImported'));
    } catch (error) {
      toast.error(t('courses:courseCreate.toasts.jsonImportError') + error.message);
    }
  };

  // (удален дубликат handleOpenLessonSelector; используем мемоизированный handleOpenLessonSelectorMemo выше)

  // Добавляем функцию для обработки выбора уроков
  const handleSelectLessons = (selectedLessons) => {
    if (currentModuleIndex === null) return;
    
    setCourseData(prevData => {
      const updatedModules = [...(prevData.modules || [])];
      const moduleRef = updatedModules[currentModuleIndex];

      if (!moduleRef) return prevData;

      // Модули всегда имеют вложенную структуру moduleRef.module
      const module = moduleRef.module || moduleRef;
      const currentLessons = module.lessons || [];
      
      // Добавляем выбранные уроки в текущий модуль
      selectedLessons.forEach(lesson => {
        // Проверяем, не добавлен ли уже этот урок
        const isLessonAlreadyAdded = currentLessons.some(
          existingLesson => existingLesson._id === lesson._id
        );
        
        if (!isLessonAlreadyAdded) {
          currentLessons.push(lesson);
        }
      });
      
      updatedModules[currentModuleIndex] = {
        ...moduleRef,
        module: {
          ...module,
          lessons: currentLessons
        }
      };
      
      return {
        ...prevData,
        modules: updatedModules
      };
    });
  };


  if (loading && initialLoad) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Typography>{t('common:loading')}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
        <Box sx={{ my: 4 }}>
          {/* Breadcrumb Navigation */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="body2"
                component="a"
                href={`/courses/${slug}`}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {t('courses:courseCreate.ui.backToCourses')}
              </Typography>
              <Typography variant="body2" color="text.secondary">/</Typography>
              <Typography variant="body2" fontWeight="600">
                {t('courses:courseCreate.ui.editTitle')}
              </Typography>
            </Stack>
          </Box>

        {/* Header with Title and Save Button */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          mb={4}
        >
          <Typography variant="h3" component="h1" fontWeight="700">
            {t('courses:courseCreate.ui.editTitle')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <JsonImportExport
              data={courseData}
              onImport={handleJsonImport}
              title={t('courses:courseCreate.ui.jsonImportExport.title')}
              description={t('courses:courseCreate.ui.jsonImportExport.description')}
              jsonStructure={{
                title: 'Название курса',
                description: 'Описание курса',
                difficulty: 'Уровень сложности (beginner, intermediate, advanced)',
                modules: []
              }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              size="large"
              startIcon={<SaveIcon />}
            >
              {submitting ? t('courses:courseCreate.ui.saving') : t('courses:courseCreate.ui.saveChangesButton')}
            </Button>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Main Content - 7 columns */}
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  overflow: 'visible' // Разрешаем dropdown выходить за пределы карточки
                }}
              >
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    required
                    label={t('courses:courseCreate.ui.courseTitle')}
                    name="title"
                    value={localFormData.title}
                    onChange={handleCourseDataChange}
                    placeholder="e.g., Dropbox Fundamentals"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={5}
                    label={t('courses:courseCreate.ui.courseDescription')}
                    name="description"
                    value={localFormData.description}
                    onChange={handleCourseDataChange}
                    placeholder="Enter the full description of the course..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3
                      }
                    }}
                  />

                  {/* Difficulty Level */}
                  <Box>
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      {t('courses:courseCreate.ui.difficulty')}
                    </Typography>
                    <Select
                      fullWidth
                      name="difficulty"
                      value={localFormData.difficulty}
                      onChange={handleCourseDataChange}
                      required
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            zIndex: 4000
                          }
                        }
                      }}
                      sx={{
                        borderRadius: 3,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: 3
                        }
                      }}
                    >
                      <MenuItem value="beginner">
                        {t('courses:courseCreate.ui.difficultyLevels.beginner')}
                      </MenuItem>
                      <MenuItem value="intermediate">
                        {t('courses:courseCreate.ui.difficultyLevels.intermediate')}
                      </MenuItem>
                      <MenuItem value="advanced">
                        {t('courses:courseCreate.ui.difficultyLevels.advanced')}
                      </MenuItem>
                    </Select>
                  </Box>

                  {/* (Tools) Section */}
                  <CustomAutocomplete
                    label={t('courses:courseCreate.ui.tools')}
                    placeholder="Select tools"
                    options={tools}
                    loading={toolsLoading}
                    onChange={handleToolsChange}
                    getOptionLabel={(option) =>
                      option.term_group?.MainTerm?.value ||
                      option.library?.name ||
                      option.name ||
                      ''
                    }
                    selectedChips={selectedTools}
                    onChipDelete={(index) => {
                      const newTools = selectedTools.filter((_, i) => i !== index);
                      handleToolsChange(newTools);
                    }}
                    chipColor="default"
                    zIndex={1}
                  />

                  {/* Professions Section with Custom Autocomplete */}
                  <CustomAutocomplete
                    label={t('courses:courseCreate.ui.professions')}
                    placeholder={t('courses:coursesPage.filters.selectProfessions')}
                    value={selectedProfessions}
                    options={professions}
                    loading={professionsLoading}
                    onChange={handleProfessionsChange}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.term_group?.MainTerm?.value ||
                             option.term_group?.name ||
                             option.name ||
                             option.title ||
                             '';
                    }}
                    selectedChips={selectedProfessions}
                    onChipDelete={(index) => {
                      const profToDelete = selectedProfessions[index];
                      const profId = String(profToDelete.id || profToDelete._id);
                      const newProfs = selectedProfessions.filter((_, i) => i !== index);
                      handleProfessionsChange(newProfs);
                      setManualProfessions(prevManual => {
                        const updated = new Set(prevManual);
                        updated.delete(profId);
                        return updated;
                      });
                    }}
                    getChipColor={(item) => {
                      const profId = String(item.id || item._id);
                      return manualProfessions.has(profId) ? 'secondary' : 'primary';
                    }}
                    zIndex={1}
                  />

                  <Alert severity="info">
                    {t('courses:courseCreate.ui.professionsAutoSync')}
                  </Alert>
                </Stack>
                </Card>
              </Grid>
              {/* Sidebar - 5 columns */}
              <Grid item xs={12} lg={5}>
                <Stack spacing={3}>
                  {/* Status Card */}
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      overflow: 'visible' // Разрешаем dropdown выходить за пределы карточки
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} mb={2}>
                      Status
                    </Typography>
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatusDropdownOpen(!statusDropdownOpen);
                        }}
                        sx={({ palette }) => ({
                          p: '0 16px',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          bgcolor: palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          height: '48px',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          }
                        })}
                      >
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: '0.875rem',
                            color: 'text.primary',
                            textTransform: 'capitalize' 
                          }}
                        >
                          {isDraft ? t('common:status.draft') : t('common:status.published')}
                        </Typography>
                        <KeyboardArrowDownIcon sx={{ 
                          fontSize: 20, 
                          color: 'text.secondary',
                          transition: 'transform 0.2s',
                          transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                        }} />
                      </Box>
                      {statusDropdownOpen && (
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            mt: 1,
                            bgcolor: 'background.paper',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 3,
                            zIndex: 3000,
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ p: 1 }}>
                            <Box
                              onClick={() => {
                                setIsDraft(true);
                                setStatusDropdownOpen(false);
                                setCourseData(prev => ({ ...prev, isDraft: true }));
                              }}
                              sx={{
                                p: 1.5,
                                cursor: 'pointer',
                                borderRadius: 2,
                                '&:hover': {
                                  bgcolor: 'action.hover'
                                }
                              }}
                            >
                              <Typography variant="body2">
                                {t('common:status.draft')}
                              </Typography>
                            </Box>
                            {(user?.userMeta?.roles?.includes('admin') || user?.userMeta?.roles?.includes('teacher')) && (
                              <Box
                                onClick={() => {
                                  setIsDraft(false);
                                  setStatusDropdownOpen(false);
                                  setCourseData(prev => ({ ...prev, isDraft: false }));
                                }}
                                sx={{
                                  p: 1.5,
                                  cursor: 'pointer',
                                  borderRadius: 2,
                                  '&:hover': {
                                    bgcolor: 'action.hover'
                                  }
                                }}
                              >
                                <Typography variant="body2">
                                  {t('common:status.published')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Card>

                  {/* Hero Section Background Card */}
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} mb={2}>
                      Hero Section Background
                    </Typography>
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 3,
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover'
                        }
                      }}
                      onClick={() => document.getElementById('course-image-upload').click()}
                    >
                      {courseData.imagePreview || courseData.image ? (
                        <img
                          src={courseData.imagePreview || courseData.image}
                          alt="Preview"
                          style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '200px',
                            objectFit: 'cover',
                            borderRadius: '12px'
                          }}
                        />
                      ) : (
                        <>
                          <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Click to upload
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            PNG, JPG (1280x485px)
                          </Typography>
                        </>
                      )}
                    </Box>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="course-image-upload"
                      type="file"
                      onChange={handleImageUpload}
                    />
                    {(courseData.imagePreview || courseData.image) && (
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={handleRemoveImage}
                        sx={{ mt: 2 }}
                      >
                        {t('courses:courseCreate.ui.removeImage')}
                      </Button>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Paste image URL..."
                      name="image"
                      value={localFormData.image}
                      onChange={handleCourseDataChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Card>

                  {/* Main Video URL Card */}
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} mb={2}>
                      Main Video URL
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Paste video URL..."
                      name="videoUrl"
                      value={localFormData.videoUrl}
                      onChange={handleVideoUrlChange}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3
                        }
                      }}
                    />
                  </Card>

                  {/* Hero Preview Button */}
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => setHeroPreviewOpen(!heroPreviewOpen)}
                    startIcon={heroPreviewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                      py: 1.5,
                      borderWidth: 2,
                      fontWeight: 600,
                      backgroundColor: 'transparent',
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                  >
                    {heroPreviewOpen ? 'Hide Hero Preview' : 'Show Hero Preview'}
                  </Button>
                </Stack>
              </Grid>
              {/* Модули, уроки и тесты — ниже, на всю ширину */}
              {ModulesSectionMemo}

              {/* Bottom Action Bar */}
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    pt: 3
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    size="large"
                    startIcon={<SaveIcon />}
                  >
                    {submitting ? t('courses:courseCreate.ui.saving') : t('courses:courseCreate.ui.saveChangesButton')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Container>
      
      {/* В конце компонента добавляем компонент LessonSelector */}
      <LessonSelector
        open={lessonSelectorOpen}
        onClose={() => setLessonSelectorOpen(false)}
        onSelectLessons={handleSelectLessons}
        selectedLessonIds={
          currentModuleIndex !== null && courseData.modules?.[currentModuleIndex]
            ? (courseData.modules[currentModuleIndex].module || courseData.modules[currentModuleIndex]).lessons?.map(lesson => lesson._id) || []
            : []
        }
      />
      
      {/* Компонент для выбора существующих модулей */}
      <ModuleSelector
        open={moduleSelectorOpen}
        onClose={() => setModuleSelectorOpen(false)}
        onSelectModules={handleSelectExistingModules}
        courseId={courseId}
      />

      {/* Модальное окно подтверждения удаления модуля */}
      <DeleteConfirmationModal
        open={deleteModuleDialog.open}
        onClose={handleCloseDeleteModuleDialog}
        onConfirm={handleConfirmDeleteModule}
        title={t('courses:courseCreate.ui.deleteModuleTitle', { title: deleteModuleDialog.moduleTitle })}
        message={t('courses:courseCreate.ui.deleteModuleMessage')}
        loading={deleteModuleDialog.loading}
      />
    </>
  );
};

export default CourseEdit;

